import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { coursesAPI } from '../services/api';
import CourseList from '../components/courses/CourseList';
import CourseForm from '../components/courses/CourseForm';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';

export default function Courses() {
  const { isAuthenticated } = useAuth();
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load courses on mount
  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setIsLoading(true);
      const response = await coursesAPI.getAll();
      setCourses(response.courses || []);
    } catch (err) {
      console.error('Error loading courses:', err);
      setError('Failed to load courses');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCourse = () => {
    setEditingCourse(null);
    setShowModal(true);
  };

  const handleEditCourse = async (course) => {
    try {
      // Fetch full course details with holes
      const response = await coursesAPI.getById(course.id);
      setEditingCourse(response.course);
      setShowModal(true);
    } catch (err) {
      console.error('Error loading course details:', err);
      alert('Failed to load course details');
    }
  };

  const handleDeleteCourse = async (course) => {
    if (!window.confirm(`Are you sure you want to delete ${course.name}? This will remove all hole data.`)) {
      return;
    }

    try {
      await coursesAPI.delete(course.id);
      await loadCourses();
    } catch (err) {
      console.error('Error deleting course:', err);
      alert('Failed to delete course: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      setError('');

      if (editingCourse) {
        // For editing, only update course name (not holes)
        await coursesAPI.update(editingCourse.id, {
          name: formData.name
        });
      } else {
        // Create new course with all holes
        await coursesAPI.create(formData);
      }

      setShowModal(false);
      setEditingCourse(null);
      await loadCourses();
    } catch (err) {
      console.error('Error saving course:', err);
      setError(err.response?.data?.error || 'Failed to save course');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditingCourse(null);
    setError('');
  };

  return (
    <div>
      {/* Header */}
      <div className="bg-white border-b border-gray-300 px-4 py-3 mb-4 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-900">Courses</h1>
        {isAuthenticated && (
          <button
            onClick={handleAddCourse}
            className="text-sm text-primary hover:underline font-semibold"
          >
            + Add Course
          </button>
        )}
      </div>

      {error && !showModal && (
        <div className="bg-red-100 text-red-800 px-6 py-4 border border-red-300 mx-4 mb-4">
          {error}
        </div>
      )}

      <CourseList
        courses={courses}
        isLoading={isLoading}
        onEdit={handleEditCourse}
        onDelete={handleDeleteCourse}
      />

      {/* Add/Edit Course Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCancel}
        title={editingCourse ? 'Edit Course' : 'Add New Course'}
        size="xl"
      >
        {error && (
          <div className="bg-red-100 text-red-800 px-4 py-3 border border-red-300 mb-4">
            {error}
          </div>
        )}

        {editingCourse && (
          <div className="bg-yellow-100 text-gray-900 px-4 py-3 border border-yellow-300 mb-4">
            <strong>Note:</strong> Editing only updates the course name.
            To modify hole details, please delete and recreate the course.
          </div>
        )}

        <div className="max-h-[70vh] overflow-y-auto pr-2">
          <CourseForm
            course={editingCourse}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isSubmitting}
          />
        </div>
      </Modal>
    </div>
  );
}
